import axios from 'axios'
import { documentContentRequest } from '@/services/api/documentContent'
import { ContractorCompany } from '@/types/contractors'
import type { DocumentStatus } from '@/types/documents'

export type User = {
  contractorCompanies: ContractorCompany[]
  firstname: string
  id: number
  isVisitor: boolean
  lastname: string
  name: string
  phone: string
}

export type MessageResponse = {
  code: number
  message: string
}

/**
 * The employee photo as login reports it: a reference to a document, never its bytes. A photo
 * uploaded before the storage cutover has no `uuid` yet, so its employee reports `photo: null`
 * exactly as an employee who never supplied one does (B40, B49).
 */
export type EmployeePhotoReference = {
  status: DocumentStatus
  uuid: string
}

export type AuthenticatedUser = User & {
  contractorEmployee: {
    id: number
    photo: EmployeePhotoReference | null
  }
}

const fetchUserByPhone = async (phone: string): Promise<User> => {
  const response = await axios.get('/api/users/' + phone)

  return response.data.data
}

export default fetchUserByPhone

export const pinReset = async (phone: string): Promise<MessageResponse> => {
  const response = await axios.post('/api/forgot-pin', {
    phone: phone,
  })

  return response.data.data
}

export const authenticateUser = async (
  phone: string,
  pin: string,
): Promise<AuthenticatedUser> => {
  const response = await axios.post('/api/auth/login', {
    phone: phone,
    pin: pin,
  })

  return response.data.data
}

/**
 * The employee photo's bytes, which login no longer carries — one request for one document,
 * asked for only when its status says there is something to fetch (B40).
 */
export const fetchUserPhotoContent = async (
  userId: number,
): Promise<{ content: string; mimeType: string | null }> => {
  const response = await axios.get<Blob>(
    `api/users/${userId}/photo/content`,
    documentContentRequest,
  )

  return {
    content: URL.createObjectURL(response.data),
    mimeType: response.data.type || null,
  }
}

export const changePin = async (newPin: string): Promise<MessageResponse> => {
  const response = await axios.post('/api/auth/change-pin', {
    pin: newPin,
  })

  return response?.data.data
}

export const checkPin = async (pin: string): Promise<MessageResponse> => {
  const response = await axios.post('/api/auth/check-pin', {
    pin,
  })

  return response.data?.data
}

export const locationAudits = {
  store: async (
    siteId: number,
    position: Position,
    action: string,
  ): Promise<string | void> => {
    try {
      const response = await axios.post(`/api/user-location-audits`, {
        action,
        location: position,
        siteId,
      })

      return response?.data?.data
    } catch {
      // fail silently
    }
  },
}
